import { retrieveFirmFilesOptions } from "@/lib/data/react-query/firm/firm-file";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type { FirmFile, MatterFile } from "@/lib/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import toast from "react-hot-toast";

interface EditedDocumentOptions {
  matterId: string;
  open: boolean;
  setMatterFile: Dispatch<SetStateAction<MatterFile | null>>;
  setFirmFile: Dispatch<SetStateAction<FirmFile | null>>;
}

export function useEditedSignatureRequestDocument({
  matterId,
  open,
  setMatterFile,
  setFirmFile,
}: EditedDocumentOptions) {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsRefreshing(false);
      setRefreshError(false);
    }
  }, [open]);

  const refreshMatterFile = useCallback(
    async (fileId: string) => {
      setIsRefreshing(true);
      setRefreshError(false);
      try {
        const options = retrieveMatterQueryOptions(matterId);
        const matter = (await queryClient.fetchQuery(options)).data;
        const updated = matter.files.find((file) => file.id === fileId);
        if (!updated) throw new Error("Edited matter file was not found");
        setMatterFile((current) =>
          current?.id === fileId ? updated : current,
        );
      } catch {
        setRefreshError(true);
        toast.error(
          "Could not load the edited document. Retry before sending.",
        );
      } finally {
        setIsRefreshing(false);
      }
    },
    [matterId, queryClient, setMatterFile],
  );

  const refreshFirmFile = useCallback(
    async (fileId: string) => {
      setIsRefreshing(true);
      setRefreshError(false);
      try {
        const options = retrieveFirmFilesOptions(open);
        const files = (await queryClient.fetchQuery(options)).data;
        const updated = files.find((file) => file.id === fileId);
        if (!updated) throw new Error("Edited firm file was not found");
        setFirmFile((current) => (current?.id === fileId ? updated : current));
      } catch {
        setRefreshError(true);
        toast.error(
          "Could not load the edited document. Retry before sending.",
        );
      } finally {
        setIsRefreshing(false);
      }
    },
    [open, queryClient, setFirmFile],
  );

  return { isRefreshing, refreshError, refreshMatterFile, refreshFirmFile };
}
